import { useQuery } from "convex/react";
import { api } from "../lib/convexApi";

export type Tier = "base" | "pro" | "security";

export type EntitlementState =
  | { kind: "loading" }
  | { kind: "no-plan"; tiers: Tier[] }
  | { kind: "ready"; tiers: Tier[]; hasBase: boolean; hasPro: boolean; hasSecurity: boolean };

// Pure mapper — undefined means the reactive query hasn't resolved yet.
// hasBase is true for ANY entitled tier (base ⊂ pro ⊂ security is the
// cumulative model — every paying tier includes "base" in its tiers array,
// so hasBase is effectively "is this org entitled to anything"). hasPro is
// true for pro AND security orgs (security implies pro), so it gates the
// review-loop features (risk briefs, remediation, GitHub App, audit export).
export function entitlementState(tiers: Tier[] | undefined): EntitlementState {
  if (tiers === undefined) return { kind: "loading" };
  if (tiers.length === 0) return { kind: "no-plan", tiers };
  return {
    kind: "ready",
    tiers,
    hasBase: tiers.includes("base"),
    hasPro: tiers.includes("pro"),
    hasSecurity: tiers.includes("security"),
  };
}

// Reactive entitlement hook. Re-renders the instant the Lemon Squeezy
// webhook upserts the subscriptions row — no token refresh, no reload.
// The api object is the untyped `anyApi` boundary re-exported from
// lib/convexApi (matching every other web-react Convex caller), so tsc
// does not have to follow the Convex server modules during the build.
export function useEntitlements(): EntitlementState {
  const tiers = useQuery(api.billing.queries.myEntitlements) as Tier[] | undefined;
  return entitlementState(tiers);
}
